import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Popconfirm, Select, Table, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { showError } from 'utils/exceptionUtils';
import {
  GET_TT_LOCATION_CONFIGS_WITH_PAGINATION,
  TtLocationConfigItem,
  TtLocationConfigPageResponse,
} from './graphql/queries/getTtLocationConfigs';
import {
  SAVE_TT_LOCATION_CONFIG,
  DELETE_TT_LOCATION_CONFIG,
  SaveTtLocationConfigInput,
} from './graphql/mutations/ttLocationConfigMutations';
import TtLocationConfigFormDrawer from './components/TtLocationConfigFormDrawer';

// 业务语义说明：
// 本表（tt_location_config）承载 TT 分拣位置配置。
// location_type 取值：1 始发地 / 2 目的地 / 3 下一站 / 4 TEMU大箱码 / 5 承运商（尾程商）/ 6 TT业务类型。
// 其中 location_type=5 是承运商（尾程商），在其下新增一条 parent_id 指向它的记录即「尾程商服务」，
// 服务的 code 会作为分拣规则第三段码（如 USMPM），label_code 是包牌展示码。
const LOCATION_TYPE_LABELS: Record<number, string> = {
  1: '始发地',
  2: '目的地',
  3: '下一站',
  4: 'TEMU大箱码',
  5: '承运商',
  6: 'TT业务类型',
};

// 筛选下拉展示全部配置类型（不过滤，便于查看/筛选任意存量记录，含始发地/目的地/TT业务类型）。
// 创建时的可选类型另在抽屉(TtLocationConfigFormDrawer)里过滤：仅 下一站/TEMU大箱码/承运商。
const LOCATION_TYPE_OPTIONS = [
  { label: '始发地', value: 1 },
  { label: '目的地', value: 2 },
  { label: '下一站', value: 3 },
  { label: 'TEMU大箱码', value: 4 },
  { label: '承运商', value: 5 },
  { label: 'TT业务类型', value: 6 },
];

type FilterValues = {
  name?: string;
  locationType?: number;
};

const TtLocationConfigPage: React.FC = () => {
  const [filterForm] = Form.useForm<FilterValues>();
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<TtLocationConfigItem | null>(null);

  const { data, loading, refetch } = useQuery<TtLocationConfigPageResponse>(
    GET_TT_LOCATION_CONFIGS_WITH_PAGINATION,
    {
      variables: {
        filter: {
          name: appliedFilters.name || undefined,
          locationType: appliedFilters.locationType ?? undefined,
          // 管理列表仅展示未删除记录（否则逻辑删的配置仍会显示）
          isDeleted: false,
        },
        pageRequest: { page: pagination.current, size: pagination.pageSize },
      },
      fetchPolicy: 'network-only',
    }
  );

  const [saveTtLocationConfig] = useMutation(SAVE_TT_LOCATION_CONFIG);
  const [deleteTtLocationConfig] = useMutation(DELETE_TT_LOCATION_CONFIG);

  const handleFilter = () => {
    const values = filterForm.getFieldsValue();
    setAppliedFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showNewDrawer = () => {
    setSelectedRecord(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = (record: TtLocationConfigItem) => {
    setSelectedRecord(record);
    setDrawerMode('edit');
    setDrawerVisible(true);
  };

  const handleDrawerSubmit = async (values: SaveTtLocationConfigInput) => {
    try {
      await saveTtLocationConfig({ variables: { input: values } });
      message.success(drawerMode === 'create' ? '创建成功' : '更新成功');
      setDrawerVisible(false);
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTtLocationConfig({ variables: { id } });
      message.success('删除成功');
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const columns: TableColumnsType<TtLocationConfigItem> = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (v: number) => <span style={{ whiteSpace: 'nowrap' }}>{v}</span>,
    },
    {
      title: '配置类型',
      dataIndex: 'locationType',
      key: 'locationType',
      render: (v: number | null) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {v != null ? (LOCATION_TYPE_LABELS[v] ?? v) : '-'}
        </span>
      ),
    },
    {
      title: '父级',
      dataIndex: 'parentId',
      key: 'parentId',
      render: (v: number | null) => <span style={{ whiteSpace: 'nowrap' }}>{v ?? '-'}</span>,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Label Code',
      dataIndex: 'labelCode',
      key: 'labelCode',
      render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      render: (v: number | null) => <span style={{ whiteSpace: 'nowrap' }}>{v ?? '-'}</span>,
    },
    {
      title: '操作人',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: any, record: TtLocationConfigItem) => (
        <div className="flex gap-2">
          <Button
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={() => showEditDrawer(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除该配置？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger style={{ paddingLeft: 0 }}>
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ], []);

  const tableData = data?.getTtLocationConfigsWithPagination?.content ?? [];
  const totalElements = data?.getTtLocationConfigsWithPagination?.totalElements ?? 0;

  const drawerInitialValues: SaveTtLocationConfigInput | undefined = selectedRecord
    ? {
        id: selectedRecord.id,
        parentId: selectedRecord.parentId ?? undefined,
        locationType: selectedRecord.locationType,
        code: selectedRecord.code,
        name: selectedRecord.name,
        labelCode: selectedRecord.labelCode ?? undefined,
        sortOrder: selectedRecord.sortOrder ?? undefined,
      }
    : undefined;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="基础配置说明"
        description={
          <div className="text-xs leading-5">
            <div>用途：维护 TT 分拣的基础项——下一站、TEMU 大箱码、承运商（及承运商下的「服务」子节点）。</div>
            <div>使用场景：作为建包 / 分拣机格口配置的下拉数据源；承运商「服务」的 Code 会作为分拣规则第三段（如 USMPM）。</div>
            <div>
              前置 / 规则：① 承运商的 Code 必须与系统枚举名一致（USPS/GOFO/SWIFTX…）；
              ② 「服务」在对应承运商下新建（配置类型选「承运商」+ 父级ID 指向该承运商），Code=第三段码、Label Code=包牌展示码；
              ③ 始发地/目的地/TT业务类型由系统维护，不在此新建；
              ④ 编辑可改全部字段，但改 Code / Label Code 会影响在用包牌与格口，请谨慎（建议删旧建新）。
            </div>
          </div>
        }
      />
      {/* 筛选区 */}
      <Form form={filterForm} layout="inline" className="py-3 flex flex-wrap gap-2">
        <Form.Item name="name" style={{ marginBottom: 0 }}>
          <Input placeholder="名称" allowClear style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="locationType" style={{ marginBottom: 0 }}>
          <Select
            placeholder="配置类型"
            options={LOCATION_TYPE_OPTIONS}
            allowClear
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" onClick={handleFilter}>查询</Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button onClick={handleReset}>重置</Button>
        </Form.Item>
      </Form>

      {/* 新建按钮 */}
      <div className="flex justify-end mb-3">
        <Button type="primary" onClick={showNewDrawer}>
          <span className="font-bold">新建</span>
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="auto"
        className="mb-4"
      />

      <TablePaginationBar
        total={totalElements}
        current={pagination.current}
        pageSize={pagination.pageSize}
        onChange={(page, size) => setPagination({ current: page, pageSize: size })}
      />

      <TtLocationConfigFormDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleDrawerSubmit}
        initialValues={drawerInitialValues}
        mode={drawerMode}
      />
    </div>
  );
};

export default TtLocationConfigPage;
